import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FaArrowLeft, FaCheckCircle, FaTimesCircle, FaStethoscope } from 'react-icons/fa';
import { getSessionHistory } from '../../services/sessionHistoryService';
import './history.scss';

interface HistoryItem {
  id: string;
  specialtyName: string;
  topicName: string;
  ipsScore: number | null;
  dataGatheringScore: number | null;
  diagnosisMgtScore: number | null;
  areasForImprovement: string;
  timestamp: string;
}

const PASS_THRESHOLD = 9;

export function History() {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        let combinedHistory: HistoryItem[] = [];

        // 1. Load from localStorage
        const localHistory = localStorage.getItem('sessionSummaries');
        if (localHistory) {
          let parsedLocalHistory = [];
          try {
            parsedLocalHistory = JSON.parse(localHistory);
            if (!Array.isArray(parsedLocalHistory)) {
              if (parsedLocalHistory && typeof parsedLocalHistory === 'object') {
                parsedLocalHistory = Object.values(parsedLocalHistory);
              } else {
                parsedLocalHistory = [];
              }
            }
            combinedHistory = parsedLocalHistory;
          } catch (e) {
            console.error('Error parsing sessionSummaries:', e);
          }
        }

        // 2. Also fetch from Supabase for logged-in users
        if (user?.id) {
          const dbRecords = await getSessionHistory(user.id);
          const dbItems: HistoryItem[] = dbRecords.map(r => ({
            id: r.id || `db-${r.created_at}`,
            specialtyName: r.specialty_name,
            topicName: r.topic_name,
            ipsScore: r.ips_score,
            dataGatheringScore: r.data_gathering_score,
            diagnosisMgtScore: r.diagnosis_mgt_score,
            areasForImprovement: r.areas_for_improvement,
            timestamp: r.created_at || new Date().toISOString(),
          }));

          // Merge and deduplicate (by topicName + close timestamps)
          const existingKeys = new Set(
            combinedHistory.map(h => {
              try {
                return `${h.topicName}-${new Date(h.timestamp).toISOString().slice(0, 16)}`;
              } catch (e) {
                return `${h.topicName}-${h.timestamp}`;
              }
            })
          );
          for (const item of dbItems) {
            let key = `${item.topicName}-${item.timestamp}`;
            try {
              key = `${item.topicName}-${new Date(item.timestamp).toISOString().slice(0, 16)}`;
            } catch (e) {
              // fallback to raw timestamp if invalid
            }

            if (!existingKeys.has(key)) {
              combinedHistory.push(item);
              existingKeys.add(key);
            }
          }
        }

        combinedHistory.sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

        setHistory(combinedHistory);
      } catch (error) {
        console.error('Error:', error);
        setError('Failed to load history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user]);

  const calculateTotalScore = (item: HistoryItem) => {
    return (
      (item.ipsScore || 0) +
      (item.dataGatheringScore || 0) +
      (item.diagnosisMgtScore || 0)
    );
  };

  const formatDate = (timestamp: string) => {
    try {
      const d = new Date(timestamp);
      if (isNaN(d.getTime())) return 'Invalid Date';
      return d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="history-page">
        <div className="history-loading">
          <div className="loading-spinner" />
          <p>Loading your history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-page">
        <div className="history-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="history-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <FaArrowLeft /> Back
        </button>
        <h1>Case History</h1>
        <span className="history-count">{history.length} cases</span>
      </div>

      {history.length === 0 ? (
        <div className="empty-state">
          <FaStethoscope className="empty-icon" />
          <h3>No cases yet</h3>
          <p>Complete your first clinical simulation to see it here.</p>
          <button onClick={() => navigate('/')}>Start a Case</button>
        </div>
      ) : (
        <div className="history-grid">
          {history.map((item) => {
            const total = calculateTotalScore(item);
            const isPassing = total >= PASS_THRESHOLD;
            const percent = Math.round((total / 12) * 100);

            return (
              <div key={item.id} className="history-card">
                <div className="card-top">
                  <div className="card-meta">
                    <span className="specialty-tag">{item.specialtyName}</span>
                    <span className="card-date">{formatDate(item.timestamp)}</span>
                  </div>
                  <div className={`mini-badge ${isPassing ? 'pass' : 'fail'}`}>
                    {isPassing ? <FaCheckCircle /> : <FaTimesCircle />}
                    {isPassing ? 'Pass' : 'Fail'}
                  </div>
                </div>

                <h3 className="card-topic">{item.topicName}</h3>

                <div className="card-scores">
                  <div className="score-bar-container">
                    <div className="score-labels">
                      <span>Score</span>
                      <span className="score-value">{total}/12</span>
                    </div>
                    <div className="score-bar">
                      <div
                        className={`score-bar-fill ${isPassing ? 'pass' : 'fail'}`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>

                  <div className="score-breakdown">
                    <div className="mini-score">
                      <span className="label">IPS</span>
                      <span className="value">{item.ipsScore ?? '-'}</span>
                    </div>
                    <div className="mini-score">
                      <span className="label">DG</span>
                      <span className="value">{item.dataGatheringScore ?? '-'}</span>
                    </div>
                    <div className="mini-score">
                      <span className="label">D&M</span>
                      <span className="value">{item.diagnosisMgtScore ?? '-'}</span>
                    </div>
                  </div>
                </div>

                {item.areasForImprovement && (
                  <div className="card-improvement">
                    <p>{item.areasForImprovement}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}